import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Pencil, Camera } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { GateMeasurement, DealProductItem } from "@shared/api";

interface SummaryStepProps {
  gates?: GateMeasurement[];
  products?: DealProductItem[];
  onEditGate?: (index: number) => void;
  onEditProduct?: (index: number) => void;
  /** Termékenkénti fotó lépésre ugrás (product flow) */
  onEditProductPhotos?: (index: number) => void;
}

export function SummaryStep({ gates = [], products = [], onEditGate, onEditProduct, onEditProductPhotos }: SummaryStepProps) {
  const { t } = useTranslation();
  const useProducts = products.length > 0;
  const items = useProducts ? products : gates;

  return (
    <div className="space-y-4 p-4">
      <div className="mb-6">
        <h2 className="text-xl font-semibold mb-2">{t("measurementExtra.summaryTitle")}</h2>
        <p className="text-sm text-muted-foreground">{t("measurementExtra.summaryIntro")}</p>
      </div>

      <div className="space-y-4">
        {useProducts
          ? (items as DealProductItem[]).map((item, index) => (
              <Card key={item.id}>
                <CardHeader>
                  <div className="flex items-center gap-2 min-w-0">
                    <CardTitle className="text-lg flex-1 min-w-0 truncate">
                      {(item as DealProductItem).productType} {(item as DealProductItem).index}
                    </CardTitle>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {onEditProductPhotos && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-9 w-9"
                          onClick={() => onEditProductPhotos(index)}
                          title={t("measurementExtra.photoLabel")}
                          aria-label={t("measurementExtra.photoLabel")}
                        >
                          <Camera className="h-4 w-4" />
                        </Button>
                      )}
                      {onEditProduct && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-9 w-9"
                          onClick={() => onEditProduct(index)}
                          title={t("common.edit")}
                          aria-label={t("common.edit")}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {(item as DealProductItem).widths && (item as DealProductItem).widths!.length > 0 && (
                    <div>
                      <span className="text-sm font-medium text-muted-foreground">{t("gateMeasurementExtra.widthsMm")}</span>
                      <div className="mt-1 flex flex-wrap gap-2">
                        {(item as DealProductItem).widths!.map((w, i) => (
                          <span key={i} className="px-2 py-1 rounded-md bg-muted text-sm">{w} mm</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {(item as DealProductItem).heights && (item as DealProductItem).heights!.length > 0 && (
                    <div>
                      <span className="text-sm font-medium text-muted-foreground">{t("gateMeasurementExtra.heightsMm")}</span>
                      <div className="mt-1 flex flex-wrap gap-2">
                        {(item as DealProductItem).heights!.map((h, i) => (
                          <span key={i} className="px-2 py-1 rounded-md bg-muted text-sm">{h} mm</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {(item as DealProductItem).surface && (
                    <div>
                      <span className="text-sm font-medium text-muted-foreground">{t("gateMeasurementExtra.surfaceLabel")}:</span>
                      <span className="ml-2 text-sm">{(item as DealProductItem).surface}</span>
                    </div>
                  )}
                  {(item as DealProductItem).color && (
                    <div>
                      <span className="text-sm font-medium text-muted-foreground">{t("gateMeasurementExtra.colorLabel")}:</span>
                      <span className="ml-2 text-sm">{(item as DealProductItem).color}</span>
                    </div>
                  )}
                  {(item as DealProductItem).prelis && (
                    <div>
                      <span className="text-sm font-medium text-muted-foreground">{t("gateMeasurementExtra.prelisLabel")}:</span>
                      <span className="ml-2 text-sm">{(item as DealProductItem).prelis}</span>
                    </div>
                  )}
                  {(item as DealProductItem).type && (
                    <div>
                      <span className="text-sm font-medium text-muted-foreground">{t("gateMeasurementExtra.typeLabel")}</span>
                      <span className="ml-2 text-sm">{(item as DealProductItem).type}</span>
                    </div>
                  )}
                  {(item as DealProductItem).power && (
                    <div>
                      <span className="text-sm font-medium text-muted-foreground">{t("gateMeasurementExtra.powerLabel")}</span>
                      <span className="ml-2 text-sm">{(item as DealProductItem).power}</span>
                    </div>
                  )}
                  {(item as DealProductItem).notes && (
                    <div>
                      <span className="text-sm font-medium text-muted-foreground">{t("gateMeasurementExtra.notesLabel")}</span>
                      <p className="mt-1 text-sm whitespace-pre-wrap">{(item as DealProductItem).notes}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          : (items as GateMeasurement[]).map((gate, index) => (
              <Card key={gate.id}>
                <CardHeader>
                  <div className="flex items-center gap-2 min-w-0">
                    <CardTitle className="text-lg flex-1 min-w-0 truncate">
                      {t("measurementExtra.gateFormHeading", { n: index + 1 })}
                    </CardTitle>
                    {onEditGate && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9 flex-shrink-0"
                        onClick={() => onEditGate(index)}
                        title={t("common.edit")}
                        aria-label={t("common.edit")}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <span className="text-sm font-medium text-muted-foreground">{t("gateMeasurementExtra.widthsMm")}</span>
                    <div className="mt-1">
                      {gate.widths.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {gate.widths.map((width, i) => (
                            <span key={i} className="px-2 py-1 rounded-md bg-muted text-sm">{width} mm</span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">{t("common.notSpecified")}</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-muted-foreground">{t("gateMeasurementExtra.heightsMm")}</span>
                    <div className="mt-1">
                      {gate.heights.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {gate.heights.map((height, i) => (
                            <span key={i} className="px-2 py-1 rounded-md bg-muted text-sm">{height} mm</span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">{t("common.notSpecified")}</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-muted-foreground">{t("gateMeasurementExtra.surfaceLabel")}:</span>
                    <span className="ml-2 text-sm">{gate.surface || t("common.notSpecified")}</span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-muted-foreground">{t("gateMeasurementExtra.colorLabel")}:</span>
                    <span className="ml-2 text-sm">{gate.color || t("common.notSpecified")}</span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-muted-foreground">{t("gateMeasurementExtra.prelisLabel")}:</span>
                    <span className="ml-2 text-sm">{gate.prelis || t("common.notSpecified")}</span>
                  </div>
                  {gate.notes && (
                    <div>
                      <span className="text-sm font-medium text-muted-foreground">{t("gateMeasurementExtra.notesLabelPlural")}</span>
                      <div className="mt-1">
                        <p className="text-sm whitespace-pre-wrap">{gate.notes}</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
      </div>
    </div>
  );
}
